import React, { useState, useEffect, useRef, useCallback } from 'react';
import Header from '../../components/Header';
import Search from '../../components/Search';
import { Link } from 'react-router-dom';
import {useRecoilValue} from "recoil";
import {fetchAPIResponseSelector} from "../../store/user.atom";
import {CategoryDTO} from "../../dtos/category.dto";
import {BaseItemDTO} from "../../dtos/item.dto";
import {useNavigate} from "react-router";
import {toast} from "react-toastify";

type Item = {
    id: string;
    name: string;
    base_price: number;
    image: string;
};

type Props = {};

const Items: React.FC<Props> = () => {
    const navigate = useNavigate();

    const [search, setSearch] = useState<string>('');
    const [items, setItems] = useState<Item[]>([]);
    const [page, setPage] = useState<number>(1);
    const [size, setSize] = useState<number>(10);
    const [totalPages, setTotalPages] = useState<number>(1);
    const observer = useRef<IntersectionObserver | null>(null);
    const fetchAPIResponse = useRecoilValue(fetchAPIResponseSelector);
    const [category, setCategory] = useState<string | undefined>(undefined);
    const [categories, setCategories] = useState<CategoryDTO[]>([]);
    const [priceIncrease, setPriceIncrease] = useState<string>('');
    const [applyingIncrease, setApplyingIncrease] = useState<boolean>(false);

    useEffect(() => {
        fetchItems(true)
    }, [category, search]);

    useEffect(() => {
        if(page !== 1) {
            fetchItems(false)
        }
    }, [page]);

    const fetchItems = async (reset: boolean = false) => {
        try {
            let fetchUrl = `/items/admin?search=${search}&size=${size}`;

            if(category !== undefined) {
                fetchUrl += `&category=${category}`
            }

            if(reset) {
                fetchUrl += `&page=1`
            } else {
                fetchUrl += `&page=${page}`
            }

            const response = await fetchAPIResponse<{
                pagination_options: any,
                contents: BaseItemDTO[]
            }>(fetchUrl, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json"
                },
            });

            setItems(prevItems => (reset ? response.data.contents : [...prevItems, ...response.data.contents]));
            setTotalPages(response.data.pagination_options.total_pages);

            if(reset) {
                setPage(1)
            }
        } catch (error) {
            console.error('Error fetching items:', error);
        }
    };

    const lastItemRef = useCallback((node: HTMLDivElement | null) => {
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && page < totalPages) {
                setPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [page, totalPages]);

    const handleEdit = (itemId: any) => {
        navigate(`/items/edit/${itemId}`);
    };

    const handleBulkPriceIncrease = async () => {
        const percentage = parseFloat(priceIncrease);

        if (isNaN(percentage) || percentage <= -100) {
            toast.error('Please enter a valid percentage.');
            return;
        }

        const confirmIncrease = window.confirm(
            `Are you sure you want to change the price of every item by ${percentage}%? This cannot be undone.`
        );
        if (!confirmIncrease) {
            return;
        }

        try {
            setApplyingIncrease(true);

            const response = await fetchAPIResponse<{ modified_count: number }>('/items/admin/bulk-price', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ percentage })
            });

            toast.success(`Updated ${response.data.modified_count} item price(s) by ${percentage}%.`);
            setPriceIncrease('');
            setPage(1);
            setItems([]);
            fetchItems(true);
        } catch (error) {
            toast.error((error as Error).message);
        } finally {
            setApplyingIncrease(false);
        }
    };

    const handleDelete = async (itemId: any) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this item?');
        if (confirmDelete) {
            try {
                await fetchAPIResponse(`/items/admin/${itemId}`, {
                    method: 'DELETE',
                    headers: {
                        'Accept': 'application/json'
                    }
                });

                setPage(1)
                setItems([])
                fetchItems(true);
            } catch (error) {
                toast.error((error as Error).message);
            }
        }
    };

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetchAPIResponse<CategoryDTO[]>('/categories', {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json"
                    },
                });

                setCategories(response.data);
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        };

        fetchCategories();
    }, []);

    return (
        <div className='flex flex-col items-center w-full h-full overflow-auto py-10'>
            <Header title={'Items'} button={true} buttonTitle={'Add item'} action={'/items/add'} />
            <div className="w-11/12 rounded-2xl bg-WhiteBg shadow-md pl-12 justify-between pr-12 mt-6 font-poppins">
                <Search search={search} setSearch={setSearch} placeholderText={'Item'}/>
                <div className='my-4 flex flex-wrap items-center justify-between gap-4'>
                    <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className='p-2 border rounded'
                    >
                        <option value="">All Categories</option>
                        {categories.map(category => (
                            <option key={category.id} value={category.id}>
                                {category.name}
                            </option>
                        ))}
                    </select>
                    <div className='flex items-center gap-2'>
                        <div className='relative'>
                            <input
                                type='number'
                                placeholder='e.g. 10'
                                value={priceIncrease}
                                onChange={(e) => setPriceIncrease(e.target.value)}
                                className='p-2 pr-7 border rounded w-32'
                            />
                            <span className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400'>%</span>
                        </div>
                        <button
                            onClick={handleBulkPriceIncrease}
                            disabled={applyingIncrease}
                            className='bg-primary text-white rounded-md p-2 disabled:opacity-50'
                        >
                            {applyingIncrease ? 'Applying...' : 'Raise all prices'}
                        </button>
                    </div>
                </div>
                <div className='w-full overflow-visible flex-grow'>
                    <div className="w-full overflow-visible">
                        <div className='w-full grid grid-cols-4 font-bold text-mainTextColor my-4'>
                            <div>Name</div>
                            <div>Price</div>
                            <div>Image</div>
                            <div>Actions</div>
                        </div>
                        <hr className='border-t border-[#cdcdd0]'/>
                        {items.map((item, index) => (
                            <div
                                key={item.id}
                                className='w-full grid grid-cols-4 my-4'
                                ref={index === items.length - 1 ? lastItemRef : null}
                            >
                                <div className='text-TextBlue flex items-center'>
                                    <p className='underline'>{item.name}</p>
                                </div>
                                <div className='text-TextBlue flex items-center'>
                                    <p className=''>${item.base_price}</p>
                                </div>
                                <div className='flex'>
                                    <img src={item.image} alt={item.name} className='w-20 h-20'/>
                                </div>
                                <div className='flex items-center space-x-4'>
                                    <button onClick={() => handleEdit(item.id)}
                                            className='text-[#1490C2] underline'>Edit
                                    </button>
                                    <button onClick={() => handleDelete(item.id)} className='text-[#C41E3A]'>Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Items;
